/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useCallback, useRef, useState, useEffect } from 'preact/hooks';
import { FieldsProps, useFormProps } from '@/auth/types';
import { initReCaptcha } from '@adobe-commerce/recaptcha';
import initializeFormDataAndErrors from '@/auth/lib/initializeFormDataAndErrors';
import {
  validationFields,
  ValidationFieldsConfig,
} from '@/auth/lib/validationFields';
import { excludedFocusClasses } from '@/auth/configs/excludedFocusClasses';
import { useCustomTranslations } from '@/auth/hooks/useCustomTranslations';

export const useForm = ({ fieldsConfig, onSubmit }: useFormProps) => {
  /**
   * useCustomTranslations is required to support extensibility of error messages.
   * Ensure all error-related translation paths include ".default"
   * to allow future handling of dynamic or nested error messages.
   */
  const translations = useCustomTranslations({
    requiredFieldError: 'Auth.FormText.requiredFieldError.default',
    lengthTextError: 'Auth.FormText.lengthTextError.default',
    numericError: 'Auth.FormText.numericError.default',
    alphaNumWithSpacesError: 'Account.FormText.alphaNumWithSpacesError.default',
    alphaNumericError: 'Auth.FormText.alphaNumericError.default',
    alphaError: 'Auth.FormText.alphaError.default',
    emailError: 'Auth.FormText.emailError.default',
    dateError: 'Auth.FormText.dateError.default',
    dateRangeError: 'Auth.FormText.dateRangeError.default',
    dateMaxError: 'Auth.FormText.dateMaxError.default',
    dateMinError: 'Auth.FormText.dateMinError.default',
    urlError: 'Auth.FormText.urlError.default',
  });

  const formRef = useRef<HTMLFormElement>(null);
  const focusExecutedRef = useRef<boolean>(false);
  const [formData, setFormData] = useState<
    Record<string, string | boolean | number>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleValidationSubmit = useCallback(() => {
    let formValid = true;
    const errorsList = { ...errors };
    let firstErrorField: string | null = null;

    for (const [name, value] of Object.entries(formData)) {
      const fieldConfig = fieldsConfig?.find((config: FieldsProps) =>
        config?.customUpperCode?.includes(name)
      );

      const validationResult = validationFields(
        value.toString(),
        fieldConfig as ValidationFieldsConfig,
        translations,
        errorsList
      );

      if (validationResult[name]) {
        Object.assign(errorsList, validationResult);
        formValid = false;
      }

      if (!firstErrorField) {
        firstErrorField =
          Object.keys(errorsList).find((key) => errorsList[key]) ?? null;
      }
    }

    setErrors(errorsList);

    if (firstErrorField && formRef.current) {
      const input = formRef.current.elements.namedItem(
        firstErrorField
      ) as HTMLElement;
      input?.focus();
    }

    return formValid;
  }, [errors, fieldsConfig, formData, translations]);

  useEffect(() => {
    if (fieldsConfig?.length) {
      const { initialData, errorList } =
        initializeFormDataAndErrors(fieldsConfig);

      setFormData((prev) => ({
        ...(initialData as Record<string, string | number>),
        ...prev,
      }));

      setErrors(errorList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fieldsConfig)]);

  const handleFocus = useCallback(async (event: FocusEvent) => {
    const target = event.target as HTMLInputElement;
    const isFocusable = !excludedFocusClasses.some((className) =>
      target.classList.contains(className)
    );

    if (!focusExecutedRef.current && isFocusable) {
      await initReCaptcha(0);
      focusExecutedRef.current = true;
    }
  }, []);

  const handleChange = useCallback(
    (event: Event) => {
      const { name, value, type, checked } = event?.target as HTMLInputElement;
      const fieldValue = type === 'checkbox' ? checked : value;

      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          [name]: fieldValue,
        };

        return updatedFormData as Record<string, string | number>;
      });

      const fieldConfig = fieldsConfig?.find((config: FieldsProps) =>
        config?.customUpperCode?.includes(name)
      );

      let errorsList = { ...errors };

      if (fieldConfig) {
        const validationResult = validationFields(
          fieldValue.toString(),
          fieldConfig as ValidationFieldsConfig,
          translations,
          errorsList
        );

        if (validationResult) {
          Object.assign(errorsList, validationResult);
        }

        setErrors(errorsList);
      }
    },
    [fieldsConfig, errors, translations]
  );

  const handleBlur = useCallback(
    (event: Event) => {
      const { name, value, type, checked } = event?.target as HTMLInputElement;
      const fieldValue = type === 'checkbox' ? checked : value;

      const fieldConfig = fieldsConfig?.find(
        (config: FieldsProps) => config.customUpperCode === name
      );

      if (fieldConfig) {
        const errorsList = { ...errors };

        const validationResult = validationFields(
          fieldValue.toString(),
          fieldConfig as ValidationFieldsConfig,
          translations,
          errorsList
        );

        if (validationResult) {
          Object.assign(errorsList, validationResult);
        }

        setErrors(errorsList);
      }
    },
    [errors, fieldsConfig, translations]
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent) => {
      event.preventDefault();

      const formValid = handleValidationSubmit();

      onSubmit?.(event, formValid);
    },
    [handleValidationSubmit, onSubmit]
  );

  return {
    formData,
    errors,
    formRef,
    handleChange,
    handleBlur,
    handleSubmit,
    handleFocus,
  };
};
